import type {
  ProdState,
  PromotionBox,
  PromotionGesture,
  SyncRunResult,
  SyncStatus,
} from '@matter_extractor/shared';
import {
  PROMOTION_ROUTES,
  prodStateSchema,
  promotionBoxSchema,
  syncRunResultSchema,
  syncStatusSchema,
} from '@matter_extractor/shared';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * The calls of the passage to production (vitrine.md section 4, lot D of issue #192): the two
 * routes the box of the cascade reads and writes, and the question « is a prod linked to this
 * server? » that decides which box a screen draws.
 *
 * ## Why a file of its own
 *
 * `lib/api.ts` is edited by the lots that touch the versioned screens in the same cycle, and two
 * lots in one module is a merge that goes wrong where nobody looks. The request helper below is
 * the one deliberate duplication, and it keeps the one behaviour that matters: `details` falls
 * back to the WHOLE body, because a business refusal answers the contract body with no `details`
 * key at all - and the screens word a refusal from its `code`.
 *
 * Every answer is PARSED before it is returned.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { ...ADMIN_INIT, ...init });
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/** `POST /api/atelier/promotion/check` - judges a gesture at blank, changes nothing anywhere (§4.3). */
export function checkPromotion(gesture: PromotionGesture): Promise<PromotionBox> {
  return request(PROMOTION_ROUTES.check, {
    method: 'POST',
    body: JSON.stringify({ gesture }),
  }).then((body) => promotionBoxSchema.parse(body));
}

/** `POST /api/atelier/promotion/apply` - passes a gesture for good, the prod first (§4.4). */
export function applyPromotion(gesture: PromotionGesture): Promise<PromotionBox> {
  return request(PROMOTION_ROUTES.apply, {
    method: 'POST',
    body: JSON.stringify({ gesture }),
  }).then((body) => promotionBoxSchema.parse(body));
}

/**
 * The state of the synchronisation (`GET /api/atelier/sync`, vitrine.md §9.3): what the banner of the
 * atelier, the screen « État de la prod » and the question « is a prod linked? » all read.
 *
 * ONE request under ONE key (`SYNC_STATUS_KEY`): the banner polls it every minute, a click on
 * « Synchroniser » writes its answer into it, and `useProdLinked` below is a PROJECTION of the very
 * same query (`select`), never a second request on the same route under another key - two keys would
 * have two caches that disagree for the length of a poll.
 */
export const SYNC_STATUS_KEY = ['sync-status'] as const;

/** The local half of « État de la prod »: the candidates and the anomalies (§9.3). */
export const PROD_STATE_KEY = ['prod-state'] as const;

/**
 * The judgments of the screen « État de la prod », one per candidate: a family of its OWN, never the
 * `['promotion-check', ...]` of the box, whose key carries an attempt counter and is dropped at once
 * (`gcTime: 0`) where these are KEPT - the last verdict a prod rendered is what the screen shows when
 * the prod stops answering.
 */
export const PROD_JUDGMENT_KEY = 'prod-judgment';

/**
 * The families of queries a passage to production can move (vitrine.md §4.2): the cascade writes the
 * marks and the pointers of every entity it takes along, so their lists, editors and « utilisé dans »
 * are read again when it went through. The four singletons (the texts of the noyaux, the models, the
 * rules, the method) ride a cascade too, and the screen « État de la prod » passes them.
 */
export const CASCADE_QUERY_PREFIXES = [
  'criteria',
  'criterion',
  'personas',
  'persona',
  'protocols',
  'protocol',
  'experiences',
  'experience',
  'noyau-texts',
  'noyau-texts-usage',
  'model-settings',
  'rule-settings',
  'method-settings',
] as const;

/** `GET /api/atelier/sync`, parsed. */
export function fetchSyncStatus(): Promise<SyncStatus> {
  return request('/api/atelier/sync').then((body) => syncStatusSchema.parse(body));
}

/** `POST /api/atelier/sync` - « Synchroniser » (§8.1): waits for the run, answers what it received. */
export function runSync(): Promise<SyncRunResult> {
  return request('/api/atelier/sync', { method: 'POST' }).then((body) =>
    syncRunResultSchema.parse(body),
  );
}

/** `GET /api/atelier/prod-state` - the entities ahead of « latest prod », and the anomalies (§9.3). */
export function fetchProdState(): Promise<ProdState> {
  return request('/api/atelier/prod-state').then((body) => prodStateSchema.parse(body));
}

/**
 * The state of the synchronisation. The banner asks it on the atelier only (a vitrine has no such
 * route), reads it again every minute - every few seconds while a run is on - and an error is no
 * answer: the banner draws nothing rather than a guess.
 */
export const SYNC_STATUS_POLL_MS = 60_000;
export const SYNC_STATUS_RUNNING_POLL_MS = 3_000;

export function useSyncStatus(enabled: boolean): UseQueryResult<SyncStatus> {
  return useQuery({
    queryKey: SYNC_STATUS_KEY,
    queryFn: fetchSyncStatus,
    enabled,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.running === true ? SYNC_STATUS_RUNNING_POLL_MS : SYNC_STATUS_POLL_MS,
  });
}

/**
 * Whether a prod is linked to this server (`PROD_ORIGIN` posed), read off the state of the
 * synchronisation (`GET /api/atelier/sync`, §9.3).
 *
 * `enabled` is how a screen asks only when it needs the answer - when the box of a passage opens -
 * so a screen that never opens one never calls. The answer cannot change under a running server,
 * hence `staleTime: Infinity`: one read for the life of the page. An error is no answer, and the
 * caller treats it as « not linked »: the local box, whose confirm the server refuses with
 * `promotion-required` if a prod is there after all - never a passage that skipped the prod.
 *
 * A projection of `useSyncStatus`: the same key, the same request, `configured` selected.
 */
export function useProdLinked(enabled: boolean): UseQueryResult<boolean> {
  return useQuery({
    queryKey: SYNC_STATUS_KEY,
    queryFn: fetchSyncStatus,
    select: (status) => status.configured,
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}
